import React, { useCallback } from "react";
import { ContentItem } from "@api";
import * as C from "@/components";
import Divider from "../Divider/Divider";

interface IBigSectionProps {
  title: string;
  titleComponent: "h1" | "h2" | "h3" | "h4";
  titleClassName?: string;
  showSectionSubtitleOnMobile?: boolean;
  showSectionBodyOnMobile?: boolean;
  children?: React.ReactNode;
  showLine?: boolean;
  pageContent: Record<string, ContentItem>;
  sectionKey?: string;
  sectionBodyClassName?: string;
  applyPadding?: boolean;
  sectionClassName?: string;
  containerClassName?: string;
  usePageSectionComponent?: boolean;
  id?: string;
}

export const BigSection = ({
  title,
  titleComponent,
  titleClassName = "",
  showSectionSubtitleOnMobile = true,
  showSectionBodyOnMobile = true,
  showLine = false,
  pageContent,
  children,
  sectionKey = "",
  sectionBodyClassName = "max-w-[486px]",
  sectionClassName = "",
  containerClassName = "",
  usePageSectionComponent = true,
  id,
}: IBigSectionProps) => {
  const getTitle = useCallback(() => {
    const titleCn = "max-w-[780px] text-center mx-auto " + titleClassName;
    switch (titleComponent) {
      case "h1":
        return (
          <h1 className={titleCn} dangerouslySetInnerHTML={{ __html: title }} />
        );
      case "h2":
        return (
          <h2
            className={`max-w-[666px] text-center mx-auto ${titleClassName}`}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        );
      case "h3":
        return (
          <h3 className={titleCn} dangerouslySetInnerHTML={{ __html: title }} />
        );
      case "h4":
        return (
          <h4 className={titleCn} dangerouslySetInnerHTML={{ __html: title }} />
        );
      default:
        return null;
    }
  }, [title, titleClassName, titleComponent]);

  return usePageSectionComponent ? (
    <C.PageSection className={sectionClassName} id={id}>
      {getTitle()}
      {showLine && <Divider />}

      {pageContent[`${sectionKey}.title`]?.data && (
        <h4
          className={`text-center text-4xl mb-6 ${
            !showSectionSubtitleOnMobile ? "hidden md:block" : ""
          }`}
        >
          {pageContent[`${sectionKey}.title`]?.data}
        </h4>
      )}
      {pageContent[`${sectionKey}.body`]?.data && (
        <p
          className={`text-center mx-auto ${sectionBodyClassName} ${
            !showSectionBodyOnMobile ? "hidden md:block" : ""
          }`}
        >
          {pageContent[`${sectionKey}.body`]?.data}
        </p>
      )}

      <div className={`py-10 ${containerClassName}`}>{children}</div>
    </C.PageSection>
  ) : (
    <div className="overflow-hidden py-10 md:py-[60px]">
      <section className={sectionClassName}>
        {getTitle()}
        {showLine && <Divider />}

        {pageContent[`${sectionKey}.title`]?.data && (
          <h4
            className={`text-center text-4xl mb-6 ${
              !showSectionSubtitleOnMobile ? "hidden md:block" : ""
            }`}
          >
            {pageContent[`${sectionKey}.title`]?.data}
          </h4>
        )}
        {pageContent[`${sectionKey}.body`]?.data && (
          <p
            className={`text-center mx-auto ${sectionBodyClassName} ${
              !showSectionBodyOnMobile ? "hidden md:block" : ""
            }`}
          >
            {pageContent[`${sectionKey}.body`]?.data}
          </p>
        )}

        <div className={`py-10 ${containerClassName}`}>{children}</div>
      </section>
    </div>
  );
};
